import { useCallback, useState } from 'react';
import { notifications } from '@mantine/notifications';
import {
  assetsApi,
  precheckImage,
  uploadErrorMessage,
  ACCEPTED_IMAGE_MIME_TYPES,
  type UploadedAsset,
} from '@/services/assets.api';

export interface ImageUploadLimits {
  maxBytes?: number;
  acceptedMimeTypes?: string[];
}

/**
 * One upload path shared by every entry point — the toolbar button, drag and
 * drop, and paste — so they cannot drift in what they accept or how they report
 * failure. The server re-validates everything regardless; the client checks
 * only to fail fast.
 */
export function useImageUpload(limits?: ImageUploadLimits) {
  const [uploading, setUploading] = useState(false);

  /**
   * Uploads a batch, returning the assets that succeeded. Failures are surfaced
   * per-file and do not abort the rest: dropping five images and losing all of
   * them because the third was a PDF would be worse than partial success.
   */
  const uploadMany = useCallback(
    async (files: File[], uploader: (file: File) => Promise<UploadedAsset>) => {
      const images = files.filter((f) => f.type.startsWith('image/'));
      if (images.length === 0) return [];

      setUploading(true);
      const uploaded: UploadedAsset[] = [];
      try {
        for (const file of images) {
          const problem = precheckImage(file, limits);
          if (problem) {
            notifications.show({
              title: file.name || 'Image rejected',
              message: problem,
              color: 'red',
            });
            continue;
          }
          try {
            uploaded.push(await uploader(file));
          } catch (error) {
            notifications.show({
              title: file.name || 'Upload failed',
              message: uploadErrorMessage(error),
              color: 'red',
            });
          }
        }
      } finally {
        setUploading(false);
      }
      return uploaded;
    },
    [limits],
  );

  /** Flow-content upload (authenticated author), used by the rich-text editor. */
  const uploadFlowContent = useCallback(
    (files: File[], flowId?: string) =>
      uploadMany(files, (file) => assetsApi.uploadFlowContent(file, flowId)),
    [uploadMany],
  );

  return { uploading, uploadMany, uploadFlowContent };
}

/**
 * Whether a drag carries files we would try to upload.
 *
 * During dragover the browser exposes only item *types*, never contents, so
 * this cannot be stricter than a MIME check — and the MIME type here comes from
 * the OS, not the bytes. It decides whether to show a drop target, nothing more.
 */
export function dragHasImages(dt: DataTransfer | null): boolean {
  if (!dt) return false;
  return Array.from(dt.items ?? []).some(
    (item) => item.kind === 'file' && item.type.startsWith('image/'),
  );
}

/** Image files from a drop or paste, filtered to what the picker would allow. */
export function imageFilesFrom(dt: DataTransfer | null): File[] {
  if (!dt) return [];
  return Array.from(dt.files ?? []).filter((f) =>
    ACCEPTED_IMAGE_MIME_TYPES.includes(f.type),
  );
}
